/**
 * RuleConditionRow — dense, single-row condition editor. Authors THREE kinds:
 *
 *   metric  →  [ Field ▼ ]  [ < ≤ = ≠ > ≥ ]  [ value ]                  [×]
 *   ranking →  [ Field ▼ ]  [ Highest… ▼ ]  [ % ]  [ Path|Flow ]        [×]
 *   tag     →  [ Field ▼ ]  [ Has | Doesn't have ]                      [×]
 *
 * The one "Field" picker groups the choices into labelled sections — "Score
 * ranking" above the metrics and "Flow tag" below them — so switching kind is a
 * single selection. The reason hint rides the row's `title` so it costs no
 * vertical space. The node written back is the discriminated RuleNode from the
 * shared foundation (`kind: condition | ranking | tag`).
 *
 * Tag names are supplied by the parent (from `useFlowTagRules(layer)`); the
 * compare-mode cluster (CompareSection) reads the live preview context itself.
 * Metrics come through the formula metric catalog (the Flow Optimizer's
 * metadata unless a provider is set); `rankingMetrics` narrows the
 * score-ranking choices ([] = none) and `allowCompare` off hides the ⇄ — the
 * Automation reuses this row with Raw score only and no compare.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { X } from 'lucide-react';
import { isRankingNode, isTagNode } from '@trafficpulse/shared';
import type { FlowLayer, FlowTagRankingMetric, RuleNode } from '@trafficpulse/shared';

import { useFormulaMetricCatalog } from '../calculation/metric-catalog/FormulaMetricCatalogContext';
import { buildMetricOptions, metricLabel } from './rule-condition/metric-options';
import MetricConditionControls from './rule-condition/MetricConditionControls';
import { OP_META } from './rule-condition/OperatorPills';
import {
  buildKindOptionGroups,
  decodePickerValue,
  pickerValue,
  type ConditionNode,
  type GroupTag,
} from './rule-condition/kind/condition-kind';
import { conditionTooltip, nodeFromSelection } from './rule-condition/kind/condition-node';
import RankingConditionControls from './rule-condition/kind/RankingConditionControls';
import TagConditionControls from './rule-condition/kind/TagConditionControls';

export interface RuleConditionRowProps {
  node: ConditionNode;
  /** Layer this group targets — drives the ranking scope toggle. Default 'landers'. */
  layer?: FlowLayer;
  /** Cube key the group's conditions read — lets compare mode show real data. */
  windowKey?: string | null;
  /** Flow tags selectable here (name + color + emoji). Default none. */
  tags?: GroupTag[];
  /** Score-ranking fields offered. Default all; [] = none. */
  rankingMetrics?: ReadonlyArray<FlowTagRankingMetric>;
  /** Offer comparing against another metric (⇄). Default true. */
  allowCompare?: boolean;
  /** The row's hover words, when the host is not the Flow Optimizer (null = the default words). */
  describe?: (node: ConditionNode) => string | null;
  onChange: (next: RuleNode) => void;
  onRemove: () => void;
}

const RuleConditionRow: FC<RuleConditionRowProps> = ({
  node,
  layer = 'landers',
  windowKey = null,
  tags = [],
  rankingMetrics,
  allowCompare = true,
  describe,
  onChange,
  onRemove,
}) => {
  const { data } = useFormulaMetricCatalog();
  const metrics = data?.metrics ?? [];
  const optionGroups = buildKindOptionGroups(
    buildMetricOptions(metrics),
    tags,
    isTagNode(node) ? node.tagName : undefined,
    rankingMetrics,
  );
  const tooltip = describe?.(node) ?? conditionTooltip(node, (id) => metricLabel(metrics, id));
  const pickField = (value: string) =>
    onChange(nodeFromSelection(decodePickerValue(value), node, layer));

  // A selected tag paints the Field <select> itself in the tag's color (emoji is
  // already in the label) — so the row shows ONE clean colored dropdown, no chip.
  const selectedTagColor = isTagNode(node)
    ? tags.find((t) => t.name === node.tagName)?.color
    : undefined;
  const selectStyle = selectedTagColor
    ? {
        backgroundColor: `${selectedTagColor}22`,
        color: selectedTagColor,
        borderColor: `${selectedTagColor}66`,
        fontWeight: 600,
      }
    : undefined;

  return (
    <div
      title={tooltip}
      className="flex min-w-0 flex-wrap items-center gap-2 rounded-md border border-border bg-card/60 px-2 py-1.5"
    >
      <select
        value={pickerValue(node)}
        onChange={(e) => pickField(e.target.value)}
        aria-label="Condition field"
        style={selectStyle}
        className="h-8 w-44 max-w-[14rem] shrink-0 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {optionGroups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((o) => (
              <option key={o.value} value={o.value} title={o.title}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {isRankingNode(node) ? (
        <RankingConditionControls node={node} layer={layer} onChange={onChange} />
      ) : isTagNode(node) ? (
        <TagConditionControls node={node} onChange={onChange} />
      ) : (
        <MetricConditionControls
          node={node}
          metrics={metrics}
          layer={layer}
          windowKey={windowKey}
          allowCompare={allowCompare}
          onChange={onChange}
        />
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove condition"
        className="ml-auto flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// Re-exported so other modules don't import the operator helpers directly.
export { OP_META };

export default RuleConditionRow;
